import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

interface IResetOfflinePasscodeRequest {
  newPasscode: string;
}

interface IResetOfflinePasscodeResponse {
  status?: boolean;
  message?: string;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IResetOfflinePasscodeResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const resetOfflinePasscodeAsync = createAsyncThunk(
  'resetOfflinePasscode',
  async (
    { newPasscode }: IResetOfflinePasscodeRequest,
    { rejectWithValue, getState }
  ) => {
    const state = getState() as RootState;

    const accessToken = state.auth.value?.data?.token?.accessToken;

    const config = {
      headers: {
        Authorization: `Token ${accessToken}`,
      },
    };

    try {
      const { data } = await api.patch(
        `/user/offline/update-passcode`,
        {
          newPasscode,
        },
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IResetOfflinePasscodeResponse>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const resetOfflinePasscodeSlice = createSlice({
  name: 'resetOfflinePasscode',
  initialState,
  reducers: {
    resetOfflinePasscodeReset: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetOfflinePasscodeAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(resetOfflinePasscodeAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IResetOfflinePasscodeResponse;
      })
      .addCase(resetOfflinePasscodeAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IResetOfflinePasscodeResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetOfflinePasscodeReset } = resetOfflinePasscodeSlice.actions;

export default resetOfflinePasscodeSlice.reducer;
